import {
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  Box,
  Alert,
  IconButton,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import emailjs from "@emailjs/browser";
import SendIcon from "@mui/icons-material/Send";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import Chemitek from "../images/chemitek.png";
import TCEnergyLogo from "../images/logo-stc.jpg";

const ContactForm = () => {
  const form = useRef();
  const [userResult, setUserResult] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  const sendEmail = () => {
    emailjs
      .sendForm(
        "service_hclqgia",
        "template_4ks5c3q",
        form.current,
        "Oyja5w33bILQvKRxv"
      )
      .then(
        () => setUserResult("Благодарим Ви за изпратеното съобщение!"),
        (error) => setUserResult("Възникна грешка, опитайте пак.")
      );
  };

  const validationSchema = yup.object({
    name: yup
      .string()
      .min(2, "Твърде кратко!")
      .max(50, "Твърде дълго!")
      .required("Задължително поле"),
    email: yup.string().email("Невалиден e-mail").required("Задължително поле"),
    phoneNumber: yup
      .string()
      .min(10, "Невалиден номер")
      .matches(
        /^((\+[1-9]{1,4}[-]*)|(\([0-9]{2,3}\)[-]*)|([0-9]{2,4})[-]*)*?[0-9]{3,4}?[-]*[0-9]{3,4}?$/,
        "Невалиден номер"
      )
      .required("Задължително поле"),
    message: yup
      .string()
      .min(10, "Твърде кратко!")
      .required("Задължително поле"),
  });

  const formik = useFormik({
    initialValues: { name: "", email: "", phoneNumber: "", message: "" },
    validationSchema,
    onSubmit: (values) => {
      sendEmail(values);
      formik.resetForm();
    },
  });

  return (
    <Grid
      container
      justifyContent="center"
      spacing={2}
      sx={{ py: 8, backgroundColor: "#f4f7fb" }}
    >
      <Grid item xs={12} md={10}>
        <Paper
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            p: 4,
            borderRadius: 3,
            boxShadow: 3,
          }}
        >
          {/* ===== ЛЯВА КОЛОНА ===== */}
          <Box sx={{ flex: 1, pr: { md: 4 }, mb: { xs: 4, md: 0 } }}>
            {/* TC Energy Лого */}
            <Box mb={3}>
              <img
                src={TCEnergyLogo}
                alt="TC Energy"
                style={{ maxWidth: "150px", height: "auto" }}
              />
            </Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", mb: 2, color: "#2f4f7f" }}
            >
              Разкажете ни повече за Вашата фотоволтаична централа
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#4a5f7a", lineHeight: 1.6, mb: 3 }}
              align="left"
            >
              Необходимостта от максимална информация относно Вашите соларни
              панели ще допринесе за най-ефективното решение за премахване на
              замърсяването и увеличаване на производителността.
            </Typography>

            {/* Контактна информация */}
            <Box sx={{ mb: 3 }}>
              <Box display="flex" alignItems="center" mb={1}>
                <PhoneIphoneIcon sx={{ color: "#2f4f7f", mr: 1 }} />
                <Typography variant="body1" color="#2f4f7f">
                  +359 884 955 699
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" mb={1}>
                <MarkEmailReadIcon sx={{ color: "#2f4f7f", mr: 1 }} />
                <Typography variant="body1" color="#2f4f7f">
                  info@tcenergy.bg
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" mb={2}>
                <LocationOnIcon sx={{ color: "#2f4f7f", mr: 1 }} />
                <Typography variant="body1" color="#2f4f7f">
                  София, България
                </Typography>
              </Box>
            </Box>

            {/* Социални мрежи */}
            <Box display="flex" gap={1} mb={3}>
              <IconButton href="#" target="_blank" sx={{ color: "#2f4f7f" }}>
                <FacebookIcon />
              </IconButton>
              <IconButton href="#" target="_blank" sx={{ color: "#2f4f7f" }}>
                <InstagramIcon />
              </IconButton>
              <IconButton href="#" target="_blank" sx={{ color: "#2f4f7f" }}>
                <LinkedInIcon />
              </IconButton>
              <Box gap={1} mt={1.5}>
                <img
                  src={Chemitek}
                  alt="Chemitek"
                  style={{ maxWidth: "150px", height: "auto" }}
                />
              </Box>
            </Box>
          </Box>

          {/* ===== ДЯСНА КОЛОНА / ФОРМА ===== */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", mb: 1, color: "#2f4f7f" }}
            >
              Свържете се с нас
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: "#4a5f7a" }}>
              В най-кратък срок ще получите обратна връзка.
            </Typography>

            {userResult && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {userResult}
              </Alert>
            )}

            <form ref={form} onSubmit={formik.handleSubmit}>
              <TextField
                fullWidth
                margin="normal"
                label="Име и фамилия"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
              <TextField
                fullWidth
                margin="normal"
                label="E-mail"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Телефон"
                name="phoneNumber"
                value={formik.values.phoneNumber}
                onChange={formik.handleChange}
                error={
                  formik.touched.phoneNumber &&
                  Boolean(formik.errors.phoneNumber)
                }
                helperText={
                  formik.touched.phoneNumber && formik.errors.phoneNumber
                }
              />
              <TextField
                fullWidth
                margin="normal"
                label="Съобщение"
                name="message"
                multiline
                rows={4}
                value={formik.values.message}
                onChange={formik.handleChange}
                error={formik.touched.message && Boolean(formik.errors.message)}
                helperText={formik.touched.message && formik.errors.message}
              />

              <Button
                type="submit"
                variant="contained"
                endIcon={<SendIcon />}
                sx={{
                  mt: 2,
                  backgroundColor: "#2f4f7f",
                  color: "#f2d35c",
                  "&:hover": {
                    backgroundColor: "#1f3558",
                    transform: "scale(1.02)",
                  },
                  fontWeight: "bold",
                  width: "100%",
                }}
              >
                Изпрати
              </Button>
            </form>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ContactForm;
